import React, { useState, useEffect, useRef, useContext } from 'react';
import AppContext from '../../contexts/AppContext';
import { User, Calendar, MessageCircle, ShoppingBag, Settings, Home, Search, Bell, Flag, Shield, Users, FileText, Plus, LogOut, Sparkles, X, Mail, Scale } from 'lucide-react';
import { logoutUser } from '../../services/authService';
import * as firestoreService from '../../services/firestoreService';
import Avatar from '../../components/common/Avatar';
import PostCreateModal from '../../components/feed/PostCreateModal';
import FeedView from '../../components/feed/FeedView';
import LeaderboardView from '../../components/discovery/LeaderboardView';
import DiscoverView from '../../components/discovery/DiscoverView';
import SearchView from '../../components/discovery/SearchView';
import EventsView from '../../components/events/EventsView';
import EventDetailView from '../../components/events/EventDetailView';
import MyEventsView from '../../components/events/MyEventsView';
import MarketplaceView from '../../components/marketplace/MarketplaceView';
import MySalesView from '../../components/marketplace/MySalesView';
import NotificationsView from '../../components/notifications/NotificationsView';
import MessagesView from '../../components/messaging/MessagesView';
import MyGroupsView from '../../components/groups/MyGroupsView';
import GroupDetailView from '../../components/groups/GroupDetailView';
import MyScorecards from '../../components/scorecards/MyScorecards';
import NewScorecardSetup from '../../components/scorecards/NewScorecardSetup';
import LiveScoreView from '../../components/scorecards/LiveScoreView';
import GameRecapView from '../../components/scorecards/GameRecapView';
import ProfileView from '../../components/profile/ProfileView';
import UserProfileView from '../../components/profile/UserProfileView';
import AdminPanel from '../../components/admin/AdminPanel';
import SettingsView from '../../components/settings/SettingsView';
import HandicapCoach from '../../components/coaching/HandicapCoach';
import CourseRecommender from '../../components/coaching/CourseRecommender';
import CaddieProGate from '../../components/coaching/CaddieProGate';
import ContactView from '../../components/contact/ContactView';
import { isUserPremium } from '../../utils/premium';

const MainApp = () => {
  const { currentUser, view, setView, showCreatePost, setShowCreatePost, setCurrentUser, messages, conversations, notifications, theme, effectiveTheme, setTheme, activeGame, selectedGameId, messagesKey, setMessagesKey } = useContext(AppContext);
  const [showMenu, setShowMenu] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showDowngradeModal, setShowDowngradeModal] = useState(false);
  const [downgrading, setDowngrading] = useState(false);
  const [systemAlert, setSystemAlert] = useState(null);
  const [showSystemAlert, setShowSystemAlert] = useState(false);
  const systemAlertRef = useRef(null);

  const checkAndShowAlert = (cfg) => {
    if (!cfg || !cfg.enabled || !cfg.message) return;
    if (cfg.expiresAt && Date.now() > new Date(cfg.expiresAt).getTime()) return;
    if (!(currentUser.seenAlerts || []).includes(cfg.createdAt)) setShowSystemAlert(true);
  };

  // Load system alert on mount
  useEffect(() => {
    firestoreService.getAppConfig('systemAlert').then(cfg => {
      if (cfg) {
        setSystemAlert(cfg);
        systemAlertRef.current = cfg;
        checkAndShowAlert(cfg);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep ref in sync with state
  useEffect(() => { systemAlertRef.current = systemAlert; }, [systemAlert]);

  const handleDismissSystemAlert = () => {
    if (systemAlert?.createdAt) {
      firestoreService.markAlertSeen(currentUser.id, systemAlert.createdAt).catch(() => {});
      setCurrentUser({
        ...currentUser,
        seenAlerts: [...(currentUser.seenAlerts || []), systemAlert.createdAt],
      });
    }
    setShowSystemAlert(false);
  };

  const handleDowngrade = async () => {
    setDowngrading(true);
    try {
      await firestoreService.updateUser(currentUser.id, { isPremium: false, trialStartDate: null });
      setCurrentUser({ ...currentUser, isPremium: false, trialStartDate: null });
      setShowDowngradeModal(false);
    } catch (err) {
      console.error('Downgrade error:', err);
    } finally {
      setDowngrading(false);
    }
  };

  // Check if there are unread messages for the current user
  const hasUnreadMessages = () => {
    return conversations.some(conv =>
      conv.participants.includes(currentUser.id) &&
      !conv.linkedGroupId &&
      !conv.linkedEventId &&
      conv.lastMessageSenderId &&
      conv.lastMessageSenderId !== currentUser.id &&
      !(conv.readBy || []).includes(currentUser.id)
    );
  };

  // Inactivity reset: go to feed if away for more than 30 minutes
  useEffect(() => {
    const LIMIT = 30 * 60 * 1000;

    const checkInactivity = () => {
      const stored = localStorage.getItem('loft_hiddenAt');
      if (stored && Date.now() - parseInt(stored) > LIMIT) {
        setView('feed');
        checkAndShowAlert(systemAlertRef.current);
      }
    };

    const saveHiddenTime = () => {
      localStorage.setItem('loft_hiddenAt', Date.now().toString());
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') {
        saveHiddenTime();
      } else {
        checkInactivity();
      }
    };

    checkInactivity();
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('beforeunload', saveHiddenTime);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('beforeunload', saveHiddenTime);
    };
  }, [setView]);

  // Close menu when clicking anywhere on the screen
  useEffect(() => {
    const handleClickOutside = () => {
      if (showMenu) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showMenu]);

  // Safety check
  if (!currentUser) {
    return null;
  }

  if ((currentUser.role === 'admin' || currentUser.role === 'superadmin' || currentUser.role === 'moderator') && view.startsWith('admin')) {
    return <AdminPanel />;
  }

  return (
    <div className="flex flex-col bg-black overflow-hidden" style={{ height: 'var(--app-height, 100vh)' }}>
      {/* Logo Bar */}
      <div className="flex-shrink-0 z-30 bg-black border-b border-gray-800 px-2 py-1 flex items-center justify-center space-x-2">
        <img src="/logos/logo-transparent-smll.png" alt="" className="h-5 w-auto object-contain" />
        <img
          src={effectiveTheme === 'dark' ? '/logos/logo-loft-dark-theme-582x243.png' : '/logos/logo-loft-light-theme-582x243.png'}
          alt="Loft"
          className="h-5 w-auto object-contain"
        />
      </div>

      {/* Nav Tabs - below logo */}
      <header className="flex-shrink-0 z-30 bg-black border-b border-gray-800">
        <nav className="flex items-center justify-center space-x-1">
          <button
            onClick={() => setView('feed')}
            className={`px-2 py-1 text-sm font-medium transition-colors ${
              view === 'feed'
                ? 'text-white border-b-2 border-white font-bold'
                : 'text-white hover:text-white'
            }`}
          >
            Feed
          </button>
          <button
            onClick={() => setView('discover')}
            className={`px-2 py-1 text-sm font-medium transition-colors ${
              view === 'discover'
                ? 'text-white border-b-2 border-white font-bold'
                : 'text-white hover:text-white'
            }`}
          >
            Discover
          </button>
          <button
            onClick={() => setView('events')}
            className={`px-2 py-1 text-sm font-medium transition-colors ${
              view === 'events'
                ? 'text-white border-b-2 border-white font-bold'
                : 'text-white hover:text-white'
            }`}
          >
            Events
          </button>
          <button
            onClick={() => setView('leaderboard')}
            className={`px-2 py-1 text-sm font-medium transition-colors ${
              view === 'leaderboard'
                ? 'text-white border-b-2 border-white font-bold'
                : 'text-white hover:text-white'
            }`}
          >
            Leaders
          </button>
          <button
            onClick={() => setView('marketplace')}
            className={`px-2 py-1 text-sm font-medium transition-colors ${
              view === 'marketplace'
                ? 'text-white border-b-2 border-white font-bold'
                : 'text-white hover:text-white'
            }`}
          >
            Market
          </button>
        </nav>
      </header>



      {/* Main content */}
      <main className="flex-1 overflow-y-auto bg-black min-h-0" style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch' }}>
        {view === 'feed' && <FeedView />}
        {view === 'search' && <SearchView />}
        {view === 'discover' && <DiscoverView />}
        {view === 'events' && <EventsView />}
        {view === 'leaderboard' && <LeaderboardView />}
        {view === 'marketplace' && <MarketplaceView />}
        {view === 'notifications' && <NotificationsView />}
        {view === 'messages' && <MessagesView key={messagesKey} />}
        {view === 'profile' && <ProfileView />}
        {view === 'settings' && <SettingsView />}
        {view === 'my-events' && <MyEventsView />}
        {view === 'my-groups' && <MyGroupsView />}
        {view === 'my-sales' && <MySalesView />}
        {view === 'my-coach' && <HandicapCoach />}
        {view === 'find-course' && <CourseRecommender />}
        {view === 'my-scorecards' && <MyScorecards />}
        {view === 'new-scorecard' && <NewScorecardSetup />}
        {view.startsWith('group-detail-') && <GroupDetailView groupId={view.replace('group-detail-', '')} />}
        {view.startsWith('event-detail-') && <EventDetailView eventId={view.replace('event-detail-', '')} />}
        {view === 'live-score' && activeGame && <LiveScoreView />}
        {view === 'game-recap' && selectedGameId && <GameRecapView />}
        {view === 'contact' && <ContactView />}
        {view.startsWith('user-profile-') && <UserProfileView userId={view.replace('user-profile-', '')} />}
      </main>

      {/* Bottom Nav */}
      <nav className="flex-shrink-0 z-30 bg-black border-t border-gray-800 px-4 py-3 flex items-center justify-center" style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}>
        <div className="flex items-center justify-around w-full max-w-2xl">
        <button onClick={() => setView('feed')} className={`${view === 'feed' ? 'text-white font-bold' : 'text-white'}`}>
          <Home className="w-6 h-6" />
        </button>
        <button onClick={() => setView('search')} className={`${view === 'search' ? 'text-white font-bold' : 'text-white'}`}>
          <Search className="w-6 h-6" />
        </button>
        <button onClick={() => setShowCreatePost(true)} className="text-white">
          <Plus className="w-6 h-6" />
        </button>
        <button onClick={() => { setView('messages'); setMessagesKey(k => k + 1); }} className={`${view === 'messages' ? 'text-white font-bold' : 'text-white'} relative`}>
          <MessageCircle className="w-6 h-6" />
          {hasUnreadMessages() && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-600 rounded-full"></span>
          )}
        </button>
        <button onClick={() => setView('notifications')} className={`${view === 'notifications' ? 'text-white font-bold' : 'text-white'} relative`}>
          <Bell className="w-6 h-6" />
          {notifications.filter(n => !n.read).length > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-600 rounded-full flex items-center justify-center text-[10px] text-pure-white font-bold px-1">
              {notifications.filter(n => !n.read).length > 99 ? '99+' : notifications.filter(n => !n.read).length}
            </span>
          )}
        </button>
        {/* Profile Avatar */}
        <div className="relative">
          <button onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}>
            <Avatar user={currentUser} size="sm" />
          </button>
          {showMenu && (
            <div className="absolute right-0 bottom-full mb-2 bg-black rounded-lg shadow-lg border border-gray-800 py-2 z-40 w-48">
              <div className="px-2 py-1 border-b border-gray-800">
                <div className="flex items-center space-x-2">
                  <p className="font-medium text-white">{currentUser.name}</p>
                  {isUserPremium(currentUser) && (
                    <button onClick={() => { setShowDowngradeModal(true); setShowMenu(false); }} className="text-xs font-semibold text-yellow-400 border border-yellow-600/50 rounded px-1.5 py-0.5 hover:bg-yellow-600/10 transition-colors">
                      Caddie Pro
                    </button>
                  )}
                </div>
                <p className="text-sm text-white">{currentUser.email}</p>
              </div>
              <button onClick={() => { setView('profile'); setShowMenu(false); }} className="w-full px-2 py-1 text-left text-white hover:bg-gray-900 flex items-center space-x-2">
                <User className="w-4 h-4" />
                <span>My Profile</span>
              </button>
              <button onClick={() => { setView('my-events'); setShowMenu(false); }} className="w-full px-2 py-1 text-left text-white hover:bg-gray-900 flex items-center space-x-2">
                <Calendar className="w-4 h-4" />
                <span>My Events</span>
              </button>
              <button onClick={() => { setView('my-scorecards'); setShowMenu(false); }} className="w-full px-2 py-1 text-left text-white hover:bg-gray-900 flex items-center space-x-2">
                <FileText className="w-4 h-4" />
                <span>My Scorecards</span>
              </button>
              <button onClick={() => { setView('new-scorecard'); setShowMenu(false); }} className="w-full px-2 py-1 text-left text-white hover:bg-gray-900 flex items-center space-x-2">
                <FileText className="w-4 h-4" />
                <span>New Scorecard</span>
              </button>
              <button onClick={() => { setView('my-groups'); setShowMenu(false); }} className="w-full px-2 py-1 text-left text-white hover:bg-gray-900 flex items-center space-x-2">
                <Users className="w-4 h-4" />
                <span>My Groups</span>
              </button>
              <button onClick={() => { setView('my-sales'); setShowMenu(false); }} className="w-full px-2 py-1 text-left text-white hover:bg-gray-900 flex items-center space-x-2">
                <ShoppingBag className="w-4 h-4" />
                <span>My Sales</span>
              </button>
              <button onClick={() => { setView('my-coach'); setShowMenu(false); }} className="w-full px-2 py-1 text-left text-white hover:bg-gray-900 flex items-center space-x-2">
                <Sparkles className="w-4 h-4" />
                <span>My Coach</span>
              </button>
              <button onClick={() => { setView('find-course'); setShowMenu(false); }} className="w-full px-2 py-1 text-left text-white hover:bg-gray-900 flex items-center space-x-2">
                <Flag className="w-4 h-4" />
                <span>Find a Golf Course</span>
              </button>
              {(currentUser.role === 'admin' || currentUser.role === 'superadmin') && (
                <button onClick={() => { setView('admin-dashboard'); setShowMenu(false); }} className="w-full px-2 py-1 text-left text-white hover:bg-gray-900 flex items-center space-x-2">
                  <Shield className="w-4 h-4" />
                  <span>Admin Panel</span>
                </button>
              )}
              {(currentUser.role === 'moderator' || currentUser.role === 'admin' || currentUser.role === 'superadmin') && (
                <button onClick={() => { setView('admin-moderation'); setShowMenu(false); }} className="w-full px-2 py-1 text-left text-white hover:bg-gray-900 flex items-center space-x-2">
                  <Flag className="w-4 h-4" />
                  <span>Moderation</span>
                </button>
              )}
              <button onClick={() => { setView('settings'); setShowMenu(false); }} className="w-full px-2 py-1 text-left text-white hover:bg-gray-900 flex items-center space-x-2">
                <Settings className="w-4 h-4" />
                <span>Settings</span>
              </button>
              {!isUserPremium(currentUser) && (
                <button onClick={() => { setShowUpgradeModal(true); setShowMenu(false); }} className="w-full px-2 py-1 text-left text-yellow-400 hover:bg-gray-900 flex items-center space-x-2">
                  <Sparkles className="w-4 h-4" />
                  <span>Upgrade to Caddie Pro</span>
                </button>
              )}
              <button onClick={() => { setView('contact'); setShowMenu(false); }} className="w-full px-2 py-1 text-left text-white hover:bg-gray-900 flex items-center space-x-2">
                <Mail className="w-4 h-4" />
                <span>Contact Us</span>
              </button>
              <button onClick={() => { setView('legal'); setShowMenu(false); }} className="w-full px-2 py-1 text-left text-white hover:bg-gray-900 flex items-center space-x-2">
                <Scale className="w-4 h-4" />
                <span>Legal</span>
              </button>
              <button onClick={async () => { try { await logoutUser(); } catch(e) {} setCurrentUser(null); }} className="w-full px-2 py-1 text-left text-red-600 hover:bg-gray-900 flex items-center space-x-2">
                <LogOut className="w-4 h-4" />
                <span>Sign Out</span>
              </button>
            </div>
          )}
        </div>
        </div>
      </nav>

      {/* Downgrade from Caddie Pro Modal */}
      {showDowngradeModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowDowngradeModal(false)}>
          <div className="relative w-full max-w-sm bg-gray-900 border border-gray-700 rounded-2xl p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-lg font-bold text-white mb-2">Downgrade to Free?</h2>
            <p className="text-gray-400 text-sm mb-4">You'll lose access to:</p>
            <ul className="text-sm text-gray-300 space-y-1.5 mb-6">
              <li className="flex items-center space-x-2"><Sparkles className="w-4 h-4 text-yellow-500 flex-shrink-0" /><span>AI coaching tips</span></li>
              <li className="flex items-center space-x-2"><Flag className="w-4 h-4 text-yellow-500 flex-shrink-0" /><span>Personalized AI course recommendations</span></li>
            </ul>
            <button
              onClick={handleDowngrade}
              disabled={downgrading}
              className="w-full py-2 bg-red-700 text-white rounded-lg font-medium hover:bg-red-800 disabled:opacity-50 mb-2"
            >
              {downgrading ? 'Downgrading...' : 'Downgrade to Free'}
            </button>
            <button onClick={() => setShowDowngradeModal(false)} className="w-full py-2 text-gray-400 hover:text-white text-sm">
              Keep Caddie Pro
            </button>
          </div>
        </div>
      )}

      {/* Upgrade to Caddie Pro Modal */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setShowUpgradeModal(false)}>
          <div className="relative w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setShowUpgradeModal(false)} className="absolute top-6 right-6 text-gray-400 hover:text-white z-10">
              <X className="w-5 h-5" />
            </button>
            <CaddieProGate />
          </div>
        </div>
      )}

      {/* System Alert Modal */}
      {showSystemAlert && systemAlert && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-md bg-gray-900 border border-yellow-600/40 rounded-2xl p-6">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center space-x-2">
                <Bell className="w-5 h-5 text-yellow-400" />
                <h3 className="text-lg font-bold text-white">Notice</h3>
              </div>
              <button onClick={handleDismissSystemAlert} className="text-gray-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">{systemAlert.message}</p>
            <button
              onClick={handleDismissSystemAlert}
              className="mt-4 w-full py-2 bg-yellow-600 text-pure-white rounded-lg font-medium hover:bg-yellow-700"
            >
              Got it
            </button>
          </div>
        </div>
      )}

      {/* Post Create Modal */}
      {showCreatePost && (
        <PostCreateModal
          onClose={() => setShowCreatePost(false)}
          currentView={view}
        />
      )}
    </div>
  );
};

export default MainApp;
